import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { 
  Lock, Mail, Eye, EyeOff, User, Sparkles, 
  ChefHat, ShieldCheck, ArrowRight, LogIn,
   PiggyBank, Crown, Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function LoginPage({ isMobile, setCurrentPage, onLogin }) {
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [particles, setParticles] = useState([]);

  // Create floating particles
  useEffect(() => {
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      speed: Math.random() * 2 + 0.5,
      color: i % 3 === 0 ? '#fbbf24' : i % 3 === 1 ? '#f97316' : '#dc2626'
    }));
    setParticles(newParticles);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "login",
        email: loginData.email,
        password: loginData.password
      });
      console.log("What Am Receiving the kackend = ", response.data);

      if (response.data.success) {
        const userData = {
          ...response.data.user,
          token: response.data.token
        };
        
        // Add success animation before login
        console.log("Received User Data = ", userData);
        setTimeout(() => {
          onLogin(userData);
        }, 500);
      } else {
        alert(response.data.message || 'Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "googleSignIn",
        googleId: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        imageUrl: decoded.picture
      });
console.log("What Am Receiving from the Backend For Google Login= ", response.data);
      if (response.data.success) {
        const userData = {
          ...response.data.user,
          token: response.data.token
        };
        console.log("Received User Data In Google Login = ", userData);
        setTimeout(() => {
          onLogin(userData);
        }, 500);
      } else {
        alert('Google login failed. Please try again.');
      }
    } catch (error) {
      console.error('Google login error:', error);
      alert('Google login failed. Please try again.');
    }
  };

  const features = [
    { icon: <ChefHat />, text: "Premium Quality Cuts" },
    { icon: <ShieldCheck />, text: "Secure Transactions" },
    { icon: <PiggyBank />, text: "Best Prices Guaranteed" },
    { icon: <Crown />, text: "VIP Member Benefits" }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-gray-900 via-black to-amber-900/20">
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(to right, #fbbf24 1px, transparent 1px),
                             linear-gradient(to bottom, #fbbf24 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        ></div>
        
        {/* Floating Particles */}
        <AnimatePresence>
          {particles.map((particle) => (
            <motion.div
              key={particle.id}
              className="absolute rounded-full"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                backgroundColor: particle.color,
              }}
              animate={{
                y: ['0vh', '-100vh'],
                x: [0, Math.random() * 100 - 50],
              }}
              transition={{
                duration: 10 / particle.speed,
                repeat: Infinity,
                ease: "linear",
                delay: particle.id * 0.1,
              }}
            />
          ))}
        </AnimatePresence>

        {/* Glowing Orbs */}
        <div className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-r from-amber-500/20 to-orange-600/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-r from-red-500/10 to-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        {/* Meat Pattern Overlay */}
        <div className="absolute inset-0 opacity-5">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                rotate: 360,
              }}
              transition={{
                duration: 20 + Math.random() * 20,
                repeat: Infinity,
                ease: "linear",
              }}
            >
              <PiggyBank className="w-8 h-8 text-amber-500/20" />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-6xl flex flex-col lg:flex-row items-center justify-center gap-12">
          {/* Left Side - Welcome & Features */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:w-1/2 text-center lg:text-left"
          >
            {/* Animated Logo/Brand */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="inline-flex items-center space-x-3 mb-8"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl blur-xl"></div>
                <div className="relative bg-gradient-to-br from-amber-600 to-orange-600 p-3 rounded-2xl">
                  <ChefHat className="w-8 h-8 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-4xl font-black bg-gradient-to-r from-amber-300 via-orange-300 to-yellow-200 bg-clip-text text-transparent">
                  Sir Henry's
                </h1>
                <p className="text-amber-400 font-bold">Premium Butchery</p>
              </div>
            </motion.div>

            {/* Welcome Text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-8"
            >
              <h2 className="text-5xl md:text-6xl font-black text-white mb-4">
                Welcome Back,
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-400">
                  Master Chef!
                </span>
              </h2>
              <p className="text-gray-300 text-lg md:text-xl">
                Access your premium account to continue your culinary journey with the finest cuts.
              </p>
            </motion.div>

            {/* Features Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-2 gap-4 mb-8"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center space-x-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 p-4 rounded-xl border border-amber-500/20 backdrop-blur-sm"
                >
                  <div className="text-amber-400">{feature.icon}</div>
                  <span className="text-gray-300 font-medium">{feature.text}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap gap-6"
            >
              {[
                { value: '15K+', label: 'Premium Members', color: 'text-amber-400' },
                { value: '4.9★', label: 'Customer Rating', color: 'text-yellow-400' },
                { value: '24/7', label: 'Support', color: 'text-green-400' },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
                  <div className="text-gray-400 text-sm">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Side - Login Form */}
          <motion.div
            initial={{ opacity: 0, x: 50, rotateY: 90 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:w-1/2 max-w-md"
          >
            {/* Glass Morphism Login Card */}
            <div className="relative group">
              {/* Glowing Border Effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-3xl blur opacity-30 group-hover:opacity-70 transition duration-1000 group-hover:duration-200"></div>
              
              {/* Main Card */}
              <div className="relative bg-gradient-to-br from-gray-900/90 via-black/80 to-gray-900/90 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 shadow-2xl">
                {/* Animated Header */}
                <motion.div
                  initial={{ y: -20 }}
                  animate={{ y: 0 }}
                  className="text-center mb-8"
                >
                  <div className="inline-flex items-center space-x-2 mb-4">
                    <Sparkles className="w-6 h-6 text-amber-400 animate-pulse" />
                    <h2 className="text-3xl font-bold text-white">Welcome Back</h2>
                    <Sparkles className="w-6 h-6 text-amber-400 animate-pulse" />
                  </div>
                  <p className="text-gray-400">Sign in to access premium features</p>
                </motion.div>

                {/* Login Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Email Input */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="relative group"
                  >
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition duration-500"></div>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-500">
                        <Mail className="w-5 h-5" />
                      </div>
                      <input
                        type="email"
                        name="email"
                        value={loginData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full pl-12 pr-4 py-4 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-500 transition-all duration-300"
                        placeholder="masterchef@example.com"
                      />
                    </div>
                  </motion.div>

                  {/* Password Input */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="relative group"
                  >
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition duration-500"></div>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-500">
                        <Lock className="w-5 h-5" />
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={loginData.password}
                        onChange={handleInputChange}
                        required
                        className="w-full pl-12 pr-12 py-4 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-500 transition-all duration-300"
                        placeholder="••••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-amber-500 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </motion.div>

                  {/* Submit Button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onHoverStart={() => setIsHovered(true)}
                    onHoverEnd={() => setIsHovered(false)}
                    type="submit"
                    disabled={isLoading}
                    className="relative w-full group"
                  >
                    {/* Animated Background */}
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-600 via-orange-500 to-red-500 rounded-xl blur group-hover:blur-xl transition-all duration-300"></div>
                    
                    {/* Button Content */}
                    <div className="relative bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-4 rounded-xl font-bold text-lg shadow-2xl shadow-amber-500/30 transition-all duration-300 flex items-center justify-center space-x-3">
                      {isLoading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Authenticating...</span>
                        </>
                      ) : (
                        <>
                          <span>ACCESS PREMIUM</span>
                          <motion.div
                            animate={isHovered ? { x: 5 } : { x: 0 }}
                            transition={{ type: "spring" }}
                          >
                            <ArrowRight className="w-5 h-5" />
                          </motion.div>
                        </>
                      )}
                    </div>

                    {/* Success Particles */}
                    <AnimatePresence>
                      {isHovered && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="absolute inset-0 overflow-hidden rounded-xl"
                        >
                          {[...Array(6)].map((_, i) => (
                            <motion.div
                              key={i}
                              className="absolute w-1 h-1 bg-yellow-300 rounded-full"
                              initial={{ x: '50%', y: '100%', opacity: 0 }}
                              animate={{
                                x: [null, Math.random() * 100 + '%'],
                                y: ['100%', '-100%'],
                                opacity: [0, 1, 0]
                              }}
                              transition={{
                                duration: 1,
                                delay: i * 0.1,
                                repeat: Infinity,
                              }}
                            />
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.button>
                </form>

                {/* Divider */}
                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-700/50"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-4 bg-gray-900 text-gray-400 text-sm font-medium">
                      OR CONTINUE WITH
                    </span>
                  </div>
                </div>

                {/* Google Login */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="flex justify-center"
                >
                  <GoogleLogin
                    clientId={GOOGLE_CLIENT_ID}
                    onSuccess={handleGoogleLogin}
                    onError={() => {
                      alert('Google login failed. Please try again.');
                    }}
                    useOneTap={false}
                    theme="filled_black"
                    size="large"
                    shape="rectangular"
                    text="signin_with"
                  />
                </motion.div>

                {/* Footer Links */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mt-8 text-center space-y-4"
                >
                  <p className="text-gray-400">
                    New to Sir Henry's?{' '}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      onClick={() => setCurrentPage('signup')}
                      className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400 font-bold hover:from-amber-300 hover:to-orange-300 transition-all duration-300"
                    >
                      Create Premium Account →
                    </motion.button>
                  </p>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    onClick={() => setCurrentPage('home')}
                    className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
                  >
                    <ArrowRight className="w-4 h-4 rotate-180" />
                    <span>Back to Homepage</span>
                  </motion.button>
                </motion.div>

                {/* Security Badge */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="mt-8 pt-6 border-t border-gray-800/50"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <ShieldCheck className="w-4 h-4 text-green-500" />
                    <span className="text-xs text-gray-400">256-bit SSL Encrypted • GDPR Compliant</span>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Floating Decorative Elements */}
      <motion.div
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 3, repeat: Infinity }}
        className="absolute top-10 right-10 hidden lg:block"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full blur-xl opacity-30"></div>
          <div className="relative bg-gradient-to-br from-amber-600 to-orange-600 p-4 rounded-full">
            <Star className="w-6 h-6 text-white" />
          </div>
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 20, 0] }}
        transition={{ duration: 4, repeat: Infinity, delay: 1 }}
        className="absolute bottom-10 left-10 hidden lg:block"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-pink-500 rounded-full blur-xl opacity-30"></div>
          <div className="relative bg-gradient-to-br from-red-600 to-pink-600 p-4 rounded-full">
            <PiggyBank className="w-6 h-6 text-white" />
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default LoginPage;